import {
  createSlice,
  createAsyncThunk,
  createListenerMiddleware,
  isAnyOf,
} from '@reduxjs/toolkit';
import { instance } from '@app/api/axios';
import { defaultTeamsParams } from '@utils/tables/params';
import { showToaster } from '@shared/ui/messages/useToaster';
import i18n from '@app/i18n';

export const fetchMyTeam = createAsyncThunk(
  'my_team/fetchMyTeam',
  async (_, { rejectWithValue, getState }) => {
    const { params, search } = getState().my_team;
    const merged = { ...defaultTeamsParams, ...params };

    const query = {
      page: merged.page,
      per_page: merged.per_page,
      sort_by: merged.sort_by,
      sort_dir: merged.sort_dir,
      ...(search && { search }),
    };

    try {
      const res = await instance.get('/my_team', { params: query });
      return res.data;
    } catch (err) {
      showToaster(
        'error',
        err?.response?.data?.message || i18n.t('request_error'),
      );
      return rejectWithValue(err.response?.data);
    }
  },
);

export const fetchAllGroups = createAsyncThunk(
  'my_team/fetchAllGroups',
  async (_, { rejectWithValue }) => {
    try {
      const res = await instance.get('/my_team');
      return res.data?.groups;
    } catch (err) {
      showToaster(
        'error',
        err?.response?.data?.message || i18n.t('request_error'),
      );
      return rejectWithValue(err.response?.data);
    }
  },
);

const myTeamSlice = createSlice({
  name: 'my_team',
  initialState: {
    groups: [],
    allGroups: [],
    allGroupsWithDeleted: [],
    count_total: 0,
    count_filtered: 0,
    params: defaultTeamsParams,
    search: '',
    activeTab: 'teams',
    isLoading: true,
    isInitialized: false,
  },
  reducers: {
    setMyTeamParams: (state, action) => {
      state.params = { ...state.params, ...action.payload };
    },
    setMyTeamSearch: (state, action) => {
      state.search = action.payload;
      state.params = { ...state.params, page: 1 };
      if (state.groups?.length === 0) {
        state.isInitialized = false;
      }
    },
    setActiveTab: (state, action) => {
      state.activeTab = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchMyTeam.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchMyTeam.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isInitialized = true;
        state.groups = action.payload?.groups;
        state.count_total = action.payload?.count_total;
        state.count_filtered = action.payload?.count_filtered;
      })
      .addCase(fetchMyTeam.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(fetchAllGroups.fulfilled, (state, action) => {
        state.allGroupsWithDeleted = action.payload;
        state.allGroups = action.payload?.filter((group) => !group.deleted_at);
      });
  },
});

export const { setMyTeamParams, setMyTeamSearch, setActiveTab } =
  myTeamSlice.actions;

export default myTeamSlice.reducer;

export const myTeamListener = createListenerMiddleware();

myTeamListener.startListening({
  matcher: isAnyOf(setMyTeamParams, setMyTeamSearch),
  effect: async (_, listenerApi) => {
    await listenerApi.dispatch(fetchMyTeam());
  },
});
